import { useState } from "react"
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"
import { Building2, Search, User, UserPlus, Users } from "lucide-react"
import type { ContactList } from "@crm-src/lib/contact-lists-api"
import type { Contact } from "@crm-src/lib/contacts-api"
import type { Customer } from "@crm-src/types/customer"
import type { Lead } from "@crm-src/types/lead"

interface SearchResult {
  key: string
  type: 'customer' | 'lead' | 'contact'
  id: number | string
  name: string
  email: string | null
  company: string | null
}

interface AddMembersDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedGroup: ContactList | null
  addMembersSearch: string
  onAddMembersSearchChange: (value: string) => void
  addMembersSearchTerm: string
  addMembersSearchLoading: boolean
  addMembersHasSearched: boolean
  filteredSearchResults: SearchResult[]
  selectedMembersToAdd: Set<string>
  onToggleMember: (type: string, id: number | string) => void
  browseLoading: boolean
  browseLoadingMore: string | null
  browseTotals: { customers: number; prospects: number; contacts: number }
  browseCustomers: Customer[]
  browseProspects: Lead[]
  browseContacts: Contact[]
  filteredBrowseCustomers: Customer[]
  filteredBrowseProspects: Lead[]
  filteredBrowseContacts: Contact[]
  onLoadMore: (type: 'customers' | 'prospects' | 'contacts') => void
  onAddMembers: () => void
}

// Per-type accent styling (kept as literal tints — file-type/category colours
// have no theme palette token).
const typeConfigs = {
  customer: { icon: Building2, color: "#2563eb", selectedBg: "#eff6ff", selectedBorder: "#bfdbfe", badgeBg: "#dbeafe", badgeFg: "#1e40af", label: "Customer" },
  lead: { icon: UserPlus, color: "#16a34a", selectedBg: "#f0fdf4", selectedBorder: "#bbf7d0", badgeBg: "#dcfce7", badgeFg: "#166534", label: "Prospect" },
  contact: { icon: User, color: "#9333ea", selectedBg: "#faf5ff", selectedBorder: "#e9d5ff", badgeBg: "#f3e8ff", badgeFg: "#6b21a8", label: "Contact" },
} as const

export function AddMembersDialog({
  open,
  onOpenChange,
  selectedGroup,
  addMembersSearch,
  onAddMembersSearchChange,
  addMembersSearchTerm,
  addMembersSearchLoading,
  addMembersHasSearched,
  filteredSearchResults,
  selectedMembersToAdd,
  onToggleMember,
  browseLoading,
  browseLoadingMore,
  browseTotals,
  browseCustomers,
  browseProspects,
  browseContacts,
  filteredBrowseCustomers,
  filteredBrowseProspects,
  filteredBrowseContacts,
  onLoadMore,
  onAddMembers,
}: AddMembersDialogProps) {
  const isSearchMode = addMembersSearchTerm.trim().length > 0
  const [browseTab, setBrowseTab] = useState<'customers' | 'prospects' | 'contacts'>('customers')

  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="md" fullWidth>
      <DialogTitle>Add Members to {selectedGroup?.name}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          Select contacts, customers, or prospects to add to this group
        </DialogContentText>

        <Box sx={{ py: 2 }}>
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search contacts..."
            value={addMembersSearch}
            onChange={(e) => onAddMembersSearchChange(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" sx={{ color: "text.disabled", display: "inline-flex" }}>
                    <Search size={16} />
                  </Box>
                </InputAdornment>
              ),
            }}
          />

          {/* Selected count */}
          {selectedMembersToAdd.size > 0 && (
            <Box sx={{ mb: 2, p: 1, bgcolor: "#fffbeb", borderRadius: 1, border: 1, borderColor: "#fde68a" }}>
              <Typography variant="body2" sx={{ color: "#92400e" }}>
                {selectedMembersToAdd.size} contact{selectedMembersToAdd.size !== 1 ? 's' : ''} selected
              </Typography>
            </Box>
          )}

          {/* Search mode: unified results */}
          {isSearchMode ? (
            addMembersSearchLoading ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" sx={{ height: 48 }} />
                ))}
              </Box>
            ) : filteredSearchResults.length === 0 && addMembersHasSearched ? (
              <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5, color: "text.disabled" }}>
                  <Users size={40} />
                </Box>
                <Typography variant="body2">No results for &quot;{addMembersSearchTerm}&quot;</Typography>
                <Typography variant="caption" sx={{ mt: 0.5, color: "text.disabled", display: "block" }}>Try a different search term</Typography>
              </Box>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 1, display: "block" }}>
                  {filteredSearchResults.length} result{filteredSearchResults.length !== 1 ? 's' : ''}
                </Typography>
                <Box sx={{ height: 288, overflow: "auto" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {filteredSearchResults.map((result) => {
                      const isSelected = selectedMembersToAdd.has(result.key)
                      const typeConfig = typeConfigs[result.type]
                      const TypeIcon = typeConfig.icon

                      return (
                        <Box
                          key={result.key}
                          onClick={() => onToggleMember(result.type, result.id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            border: 1,
                            borderColor: isSelected ? typeConfig.selectedBorder : "divider",
                            borderRadius: 1,
                            cursor: "pointer",
                            transition: "background-color .2s",
                            bgcolor: isSelected ? typeConfig.selectedBg : "transparent",
                            "&:hover": { bgcolor: isSelected ? typeConfig.selectedBg : "surfaceSoft" },
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => { }}
                            sx={{ p: 0 }}
                          />
                          <Box component="span" sx={{ color: typeConfig.color, display: "inline-flex", flexShrink: 0 }}>
                            <TypeIcon size={16} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{result.name || 'Unknown'}</Typography>
                              <Box component="span" sx={{ fontSize: "10px", px: 0.75, py: 0, borderRadius: 1, bgcolor: typeConfig.badgeBg, color: typeConfig.badgeFg, fontWeight: 500 }}>
                                {typeConfig.label}
                              </Box>
                            </Box>
                            <Typography variant="caption" noWrap sx={{ color: "text.secondary", display: "block" }}>
                              {result.email || 'No email'}
                              {result.company && <> &middot; {result.company}</>}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              </>
            )
          ) : (
            /* Browse mode: tabs */
            browseLoading ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} variant="rounded" sx={{ height: 48 }} />
                ))}
              </Box>
            ) : (
              <Box sx={{ width: "100%" }}>
                <Tabs
                  value={browseTab}
                  onChange={(_, v) => setBrowseTab(v)}
                  variant="fullWidth"
                  sx={{ minHeight: "auto", mb: 1 }}
                >
                  <Tab
                    value="customers"
                    sx={{ fontSize: "0.75rem", minHeight: "auto", textTransform: "none" }}
                    icon={<Building2 size={12} />}
                    iconPosition="start"
                    label={`Customers (${browseTotals.customers})`}
                  />
                  <Tab
                    value="prospects"
                    sx={{ fontSize: "0.75rem", minHeight: "auto", textTransform: "none" }}
                    icon={<UserPlus size={12} />}
                    iconPosition="start"
                    label={`Prospects (${browseTotals.prospects})`}
                  />
                  <Tab
                    value="contacts"
                    sx={{ fontSize: "0.75rem", minHeight: "auto", textTransform: "none" }}
                    icon={<User size={12} />}
                    iconPosition="start"
                    label={`Contacts (${browseTotals.contacts})`}
                  />
                </Tabs>

                {browseTab === 'customers' && (
                  <Box sx={{ height: 256, overflow: "auto" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {filteredBrowseCustomers.map((customer) => (
                        <Box
                          key={customer.id}
                          onClick={() => onToggleMember('customer', customer.id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            border: 1,
                            borderColor: selectedMembersToAdd.has(`customer_${customer.id}`) ? typeConfigs.customer.selectedBorder : "divider",
                            borderRadius: 1,
                            cursor: "pointer",
                            bgcolor: selectedMembersToAdd.has(`customer_${customer.id}`) ? typeConfigs.customer.selectedBg : "transparent",
                            "&:hover": { bgcolor: selectedMembersToAdd.has(`customer_${customer.id}`) ? typeConfigs.customer.selectedBg : "surfaceSoft" },
                          }}
                        >
                          <Checkbox
                            checked={selectedMembersToAdd.has(`customer_${customer.id}`)}
                            onChange={() => { }}
                            sx={{ p: 0 }}
                          />
                          <Box component="span" sx={{ color: typeConfigs.customer.color, display: "inline-flex" }}>
                            <Building2 size={16} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{customer.full_name || customer.company_name}</Typography>
                            <Typography variant="caption" noWrap sx={{ color: "text.secondary", display: "block" }}>{customer.email || 'No email'}</Typography>
                          </Box>
                        </Box>
                      ))}
                      {filteredBrowseCustomers.length === 0 && (
                        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                          <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.disabled" }}>
                            <Building2 size={32} />
                          </Box>
                          <Typography variant="body2">No customers available</Typography>
                        </Box>
                      )}
                      {browseCustomers.length < browseTotals.customers && (
                        <Button
                          size="small"
                          fullWidth
                          sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                          onClick={() => onLoadMore('customers')}
                          disabled={browseLoadingMore === 'customers'}
                        >
                          {browseLoadingMore === 'customers' ? 'Loading...' : `Load more (${browseCustomers.length} of ${browseTotals.customers})`}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

                {browseTab === 'prospects' && (
                  <Box sx={{ height: 256, overflow: "auto" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {filteredBrowseProspects.map((prospect) => (
                        <Box
                          key={prospect.id}
                          onClick={() => onToggleMember('lead', prospect.id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            border: 1,
                            borderColor: selectedMembersToAdd.has(`lead_${prospect.id}`) ? typeConfigs.lead.selectedBorder : "divider",
                            borderRadius: 1,
                            cursor: "pointer",
                            bgcolor: selectedMembersToAdd.has(`lead_${prospect.id}`) ? typeConfigs.lead.selectedBg : "transparent",
                            "&:hover": { bgcolor: selectedMembersToAdd.has(`lead_${prospect.id}`) ? typeConfigs.lead.selectedBg : "surfaceSoft" },
                          }}
                        >
                          <Checkbox
                            checked={selectedMembersToAdd.has(`lead_${prospect.id}`)}
                            onChange={() => { }}
                            sx={{ p: 0 }}
                          />
                          <Box component="span" sx={{ color: typeConfigs.lead.color, display: "inline-flex" }}>
                            <UserPlus size={16} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{prospect.full_name || prospect.company_name}</Typography>
                            <Typography variant="caption" noWrap sx={{ color: "text.secondary", display: "block" }}>{prospect.email || 'No email'}</Typography>
                          </Box>
                        </Box>
                      ))}
                      {filteredBrowseProspects.length === 0 && (
                        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                          <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.disabled" }}>
                            <UserPlus size={32} />
                          </Box>
                          <Typography variant="body2">No prospects available</Typography>
                        </Box>
                      )}
                      {browseProspects.length < browseTotals.prospects && (
                        <Button
                          size="small"
                          fullWidth
                          sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                          onClick={() => onLoadMore('prospects')}
                          disabled={browseLoadingMore === 'prospects'}
                        >
                          {browseLoadingMore === 'prospects' ? 'Loading...' : `Load more (${browseProspects.length} of ${browseTotals.prospects})`}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

                {browseTab === 'contacts' && (
                  <Box sx={{ height: 256, overflow: "auto" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {filteredBrowseContacts.map((contact) => (
                        <Box
                          key={contact.id}
                          onClick={() => onToggleMember('contact', contact.id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            border: 1,
                            borderColor: selectedMembersToAdd.has(`contact_${contact.id}`) ? typeConfigs.contact.selectedBorder : "divider",
                            borderRadius: 1,
                            cursor: "pointer",
                            bgcolor: selectedMembersToAdd.has(`contact_${contact.id}`) ? typeConfigs.contact.selectedBg : "transparent",
                            "&:hover": { bgcolor: selectedMembersToAdd.has(`contact_${contact.id}`) ? typeConfigs.contact.selectedBg : "surfaceSoft" },
                          }}
                        >
                          <Checkbox
                            checked={selectedMembersToAdd.has(`contact_${contact.id}`)}
                            onChange={() => { }}
                            sx={{ p: 0 }}
                          />
                          <Box component="span" sx={{ color: typeConfigs.contact.color, display: "inline-flex" }}>
                            <User size={16} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{contact.full_name}</Typography>
                            <Typography variant="caption" noWrap sx={{ color: "text.secondary", display: "block" }}>{contact.email || 'No email'}</Typography>
                          </Box>
                        </Box>
                      ))}
                      {filteredBrowseContacts.length === 0 && (
                        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                          <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.disabled" }}>
                            <User size={32} />
                          </Box>
                          <Typography variant="body2">No contacts available</Typography>
                        </Box>
                      )}
                      {browseContacts.length < browseTotals.contacts && (
                        <Button
                          size="small"
                          fullWidth
                          sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                          onClick={() => onLoadMore('contacts')}
                          disabled={browseLoadingMore === 'contacts'}
                        >
                          {browseLoadingMore === 'contacts' ? 'Loading...' : `Load more (${browseContacts.length} of ${browseTotals.contacts})`}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onAddMembers}
          disabled={selectedMembersToAdd.size === 0}
        >
          Add {selectedMembersToAdd.size} Member{selectedMembersToAdd.size !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
